/*
Utilities for state management.
*/

/**
 * Address a certain reducer with an action.
 * @param {string} key Key of the reducer in the global state
 * @param {Object} action Action that should address the reducer
 * @returns {Object} The updated action
 */
export function addressReducer(key, action) {
    return {
        ...action,
        reducer: key,
    }
}

/**
 * Higher order reducer to combine a global reducer with multiple extension reducers. The global reducer
 * has access to the whole state object and can override changes made by the extensions.
 * @param {(state, action) => state} global Reducer for the global state
 * @param {Object} extensions Object of extension reducers, like you would pass it to combineReducers
 */
export function extendReducer(global, extensions = {}) {
    return (state, action) => {
        if (!state) {
            // Initial action
            const extensionsInitialStates = {}
            for (const key in extensions) {
                extensionsInitialStates[key] = extensions[key](undefined, action)
            }
            const globalInitialState = global(undefined, action)
            return {
                ...extensionsInitialStates,
                ...globalInitialState,
            }
        }

        const updatedState = { ...state }
        for (const key in extensions) {
            // Skip if the action is addressed to another reducer
            if (action.reducer && action.reducer !== key) continue

            updatedState[key] = extensions[key](state[key], action)
        }
        return global(updatedState, action)
    }
}

/**
 * Creates a combined / updated index if changes happen (swallows "empty" updates).
 * @param {Object} currentIndex
 * @param {Object} [update] Index with additional entities or entities to update
 * @returns {Object} A new, combined index or the original object passed as currentIndex
 */
export function updateIndex(currentIndex, update) {
    return update ? { ...currentIndex, ...update } : currentIndex
}

/**
 * @param {*} initialState
 * @param {string} toggleActionType Type of the action, that should toggle state
 */
export function createToggleReducer(initialState, toggleActionType) {
    return (state = initialState, action) => {
        return (action.type === toggleActionType) ? action.active : state
    }
}

/**
 * @param {*} initialState
 * @param {string} enableAction Type of the action, that should enable state
 * @param {string} disableAction Type of the action, that should disable state
 */
export function createEnableDisableReducer(initialState, enableAction, disableAction) {
    return (state = initialState, action) => {
        switch (action.type) {
            case enableAction:
                return true

            case disableAction:
                return false

            default:
                return state
        }
    }
}
